import { useState, useMemo } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  Download,
  FileText,
  Loader2,
  AlertCircle,
  CheckSquare,
  Square,
  Printer,
  Mail,
  Send,
} from 'lucide-react'

import { Button } from '@payroll/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@payroll/components/ui/card'
import { Select } from '@payroll/components/ui/select'
import { Badge } from '@payroll/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@payroll/components/ui/table'

import { api } from '@payroll/utils/api'
import { formatCurrency, formatMonthEn, formatDate } from '@payroll/utils/format'
import type { PayrollPeriod, PayrollEntry, Employee } from '@payroll/types'
import { MONTHS_EN } from '@payroll/types'
import { generateSalarySlips, generateSalarySlip, type SalarySlipData } from '@payroll/utils/salary-slip-pdf'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PayrollEntryWithEmployee extends PayrollEntry {
  employee?: Employee
  employee_code?: string
  nickname?: string
  first_name_th?: string
  last_name_th?: string
  first_name_en?: string
  last_name_en?: string
  department?: string
  position?: string
  ss_exempt?: boolean | number
  company_tax_id?: string
  bank_name?: string
  bank_account?: string
  national_id?: string
}

/** Normalize flat API response into entry with nested employee object */
function normalizeEntry(raw: any): PayrollEntryWithEmployee {
  if (raw.employee) return raw
  if (raw.employee_code) {
    const {
      employee_code, nickname, title_th, first_name_th, last_name_th,
      first_name_en, last_name_en, department, position, ss_exempt,
      company_tax_id, bank_name, bank_account, national_id,
      ...entry
    } = raw
    return {
      ...entry,
      employee: {
        id: raw.employee_id,
        employee_code, nickname, title_th, first_name_th, last_name_th,
        first_name_en, last_name_en, department, position,
        ss_exempt: ss_exempt === 1 || ss_exempt === true,
        company_tax_id, bank_name, bank_account, national_id,
      } as Employee,
    }
  }
  return raw
}

function normalizeEntries(data: any[]): PayrollEntryWithEmployee[] {
  return (data ?? []).map(normalizeEntry)
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const currentYear = new Date().getFullYear()
const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i)

function entryToSlipData(entry: PayrollEntryWithEmployee, period: PayrollPeriod): SalarySlipData {
  const emp = entry.employee
  const nameEn = emp
    ? [emp.first_name_en, emp.last_name_en].filter(Boolean).join(' ')
    : ''
  const nameTh = emp
    ? [emp.title_th, emp.first_name_th, emp.last_name_th].filter(Boolean).join(' ')
    : ''

  return {
    employee_code: emp?.employee_code || '',
    nickname: emp?.nickname || undefined,
    name_en: nameEn,
    name_th: nameTh,
    department: emp?.department || '',
    position: emp?.position || undefined,
    bank_name: emp?.bank_name || undefined,
    bank_account: emp?.bank_account || undefined,
    company_tax_id: emp?.company_tax_id || '',
    national_id: emp?.national_id || '',
    pay_date: formatDate(period.pay_date),
    period: `${formatMonthEn(period.month)} ${period.year}`,

    // Income
    base_salary: entry.base_salary || 0,
    overtime: entry.overtime || 0,
    bonus: entry.bonus || 0,
    commission: entry.commission || 0,
    other_income: entry.other_income || 0,
    total_income: entry.total_income || 0,

    // Deductions
    absent_deduction: entry.absent_deduction || 0,
    leave_deduction: entry.leave_deduction || 0,
    late_deduction: entry.late_deduction || 0,
    income_tax: entry.income_tax || 0,
    social_security: entry.social_security || 0,
    other_deduction: entry.other_deduction || 0,
    total_deduction: entry.total_deduction || 0,

    net_pay: entry.net_pay || 0,

    // YTD
    ytd_income: entry.ytd_income || 0,
    ytd_tax: entry.ytd_tax || 0,
    ytd_ss: entry.ytd_social_security || 0,
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function SalarySlipsPage() {
  const [selectedYear, setSelectedYear] = useState(currentYear)
  const [selectedPeriodId, setSelectedPeriodId] = useState<string>('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  // Fetch periods
  const { data: periodsRes, isLoading: periodsLoading } = useQuery({
    queryKey: ['payroll-periods', selectedYear],
    queryFn: () => api.get<PayrollPeriod[]>(`/payroll/periods?year=${selectedYear}`),
  })

  const periods = useMemo(() => {
    const data = periodsRes?.data
    return Array.isArray(data) ? data : []
  }, [periodsRes])

  // Selected period object
  const selectedPeriod = useMemo(
    () => periods.find((p) => p.id === selectedPeriodId),
    [periods, selectedPeriodId],
  )

  // Fetch entries for the selected period
  const { data: entriesRes, isLoading: entriesLoading } = useQuery({
    queryKey: ['payroll-entries', selectedPeriodId],
    queryFn: () => api.get<PayrollEntry[]>(`/payroll/periods/${selectedPeriodId}/entries`),
    enabled: !!selectedPeriodId,
  })

  const entries = useMemo(() => {
    const data = entriesRes?.data
    return normalizeEntries(Array.isArray(data) ? data : [])
  }, [entriesRes])

  // ---------------------------------------------------------------------------
  // Selection logic
  // ---------------------------------------------------------------------------

  const allSelected = entries.length > 0 && selectedIds.size === entries.length
  const someSelected = selectedIds.size > 0 && selectedIds.size < entries.length

  function toggleAll() {
    if (allSelected) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(entries.map((e) => e.id)))
    }
  }

  function toggleOne(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // ---------------------------------------------------------------------------
  // Generate slips
  // ---------------------------------------------------------------------------

  function handleDownloadSelected() {
    if (!selectedPeriod || selectedIds.size === 0) return
    const selected = entries.filter((e) => selectedIds.has(e.id))
    const slips = selected.map((e) => entryToSlipData(e, selectedPeriod))
    generateSalarySlips(slips)
  }

  function handleDownloadAll() {
    if (!selectedPeriod || entries.length === 0) return
    const slips = entries.map((e) => entryToSlipData(e, selectedPeriod))
    generateSalarySlips(slips)
  }

  // Email mutations
  const [maxViews, setMaxViews] = useState(3)
  const [emailStatus, setEmailStatus] = useState<{ sent: number; failed: number; details: any[] } | null>(null)
  const emailMutation = useMutation({
    mutationFn: async (employeeIds?: string[]) => {
      if (!selectedPeriod) throw new Error('No period selected')
      const body: Record<string, any> = { max_views: maxViews }
      if (employeeIds?.length) body.employee_ids = employeeIds
      return api.post<any>(`/payroll/periods/${selectedPeriod.id}/email-slips`, body)
    },
    onSuccess: (res) => {
      if (res.data) {
        setEmailStatus(res.data)
      }
    },
  })

  function handleEmailSelected() {
    if (!selectedPeriod || selectedIds.size === 0) return
    const empIds = entries.filter(e => selectedIds.has(e.id)).map(e => e.employee_id)
    emailMutation.mutate(empIds)
  }

  function handleEmailAll() {
    if (!selectedPeriod || entries.length === 0) return
    emailMutation.mutate(undefined)
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Salary Slips / ใบจ่ายเงินเดือน
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Generate and print salary slips for employees
          </p>
        </div>
        <FileText className="h-8 w-8 text-[#1a237e] opacity-60" />
      </div>

      {/* Period selector */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Select Period</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            {/* Year */}
            <div className="w-36">
              <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
              <Select
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(Number(e.target.value))
                  setSelectedPeriodId('')
                  setSelectedIds(new Set())
                }}
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </Select>
            </div>

            {/* Period (Month) */}
            <div className="w-64">
              <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
              <Select
                value={selectedPeriodId}
                onChange={(e) => {
                  setSelectedPeriodId(e.target.value)
                  setSelectedIds(new Set())
                }}
                disabled={periodsLoading}
              >
                <option value="">-- Select Period --</option>
                {periods.map((p) => (
                  <option key={p.id} value={p.id}>
                    {formatMonthEn(p.month)} {p.year}
                    {p.status !== 'draft' ? ` (${p.status})` : ''}
                  </option>
                ))}
              </Select>
            </div>

            {selectedPeriod && (
              <Badge
                variant={
                  selectedPeriod.status === 'paid' || selectedPeriod.status === 'emailed'
                    ? 'default'
                    : selectedPeriod.status === 'finalized'
                    ? 'secondary'
                    : 'outline'
                }
                className="mb-0.5"
              >
                {selectedPeriod.status.toUpperCase()}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Entries table */}
      {selectedPeriodId && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">
                Employees
                {entries.length > 0 && (
                  <span className="ml-2 text-sm font-normal text-gray-500">
                    ({entries.length} employee{entries.length !== 1 ? 's' : ''})
                  </span>
                )}
              </CardTitle>

              {/* Action buttons */}
              <div className="flex gap-2 flex-wrap items-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadSelected}
                  disabled={selectedIds.size === 0}
                >
                  <Printer className="h-4 w-4 mr-1" />
                  Print ({selectedIds.size})
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadAll}
                  disabled={entries.length === 0}
                >
                  <Download className="h-4 w-4 mr-1" />
                  Print All
                </Button>

                {/* Max views control */}
                <div className="flex items-center gap-1 border rounded-md px-2 py-1 bg-gray-50">
                  <label className="text-xs text-gray-500 whitespace-nowrap" title="Maximum number of times the salary slip link can be viewed">
                    Views:
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={maxViews}
                    onChange={(e) => setMaxViews(Math.min(10, Math.max(1, parseInt(e.target.value) || 3)))}
                    className="w-10 text-center text-sm border-none bg-transparent outline-none font-medium"
                    title="Max views per slip link (1-10)"
                  />
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleEmailSelected}
                  disabled={selectedIds.size === 0 || emailMutation.isPending}
                >
                  {emailMutation.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Mail className="h-4 w-4 mr-1" />}
                  Email ({selectedIds.size})
                </Button>
                <Button
                  size="sm"
                  onClick={handleEmailAll}
                  disabled={entries.length === 0 || emailMutation.isPending}
                  className="bg-[#1a237e] hover:bg-[#283593] text-white"
                >
                  {emailMutation.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                  Email All
                </Button>
              </div>
            </div>
          </CardHeader>

          {/* Email status banner */}
          {emailStatus && (
            <div className={`mx-6 mb-2 rounded-md p-3 text-sm ${emailStatus.failed > 0 ? 'bg-amber-50 border border-amber-200 text-amber-800' : 'bg-green-50 border border-green-200 text-green-800'}`}>
              <strong>Email sent:</strong> {emailStatus.sent} success, {emailStatus.failed} failed out of {emailStatus.sent + emailStatus.failed} total
              {emailStatus.details?.filter((d: any) => !d.success).map((d: any, i: number) => (
                <div key={i} className="text-xs mt-1 text-red-600">{d.employee}: {d.error}</div>
              ))}
              <button onClick={() => setEmailStatus(null)} className="ml-3 underline text-xs">dismiss</button>
            </div>
          )}
          {emailMutation.error && (
            <div className="mx-6 mb-2 rounded-md p-3 text-sm bg-red-50 border border-red-200 text-red-700">
              Email failed: {(emailMutation.error as Error).message}
              <button onClick={() => emailMutation.reset()} className="ml-3 underline text-xs">dismiss</button>
            </div>
          )}

          <CardContent>
            {entriesLoading ? (
              <div className="flex items-center justify-center py-12 text-gray-500">
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Loading entries...
              </div>
            ) : entries.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <AlertCircle className="h-8 w-8 mb-2" />
                <p>No payroll entries found for this period.</p>
              </div>
            ) : (
              <div className="border rounded-md overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="w-10">
                        <button
                          onClick={toggleAll}
                          className="flex items-center justify-center p-0.5 hover:bg-gray-200 rounded"
                          title={allSelected ? 'Deselect all' : 'Select all'}
                        >
                          {allSelected ? (
                            <CheckSquare className="h-4 w-4 text-[#1a237e]" />
                          ) : someSelected ? (
                            <CheckSquare className="h-4 w-4 text-gray-400" />
                          ) : (
                            <Square className="h-4 w-4 text-gray-400" />
                          )}
                        </button>
                      </TableHead>
                      <TableHead className="text-xs">Code</TableHead>
                      <TableHead className="text-xs">Nickname</TableHead>
                      <TableHead className="text-xs">Name</TableHead>
                      <TableHead className="text-xs">Department</TableHead>
                      <TableHead className="text-xs text-right">Salary</TableHead>
                      <TableHead className="text-xs text-right">Total Income</TableHead>
                      <TableHead className="text-xs text-right">Deductions</TableHead>
                      <TableHead className="text-xs text-right">Net Pay</TableHead>
                      <TableHead className="text-xs text-center">Slip</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entries.map((entry) => {
                      const emp = entry.employee
                      const isSelected = selectedIds.has(entry.id)
                      return (
                        <TableRow
                          key={entry.id}
                          className={`cursor-pointer hover:bg-blue-50 ${isSelected ? 'bg-blue-50/60' : ''}`}
                          onClick={() => toggleOne(entry.id)}
                        >
                          <TableCell>
                            <span className="flex items-center justify-center">
                              {isSelected ? (
                                <CheckSquare className="h-4 w-4 text-[#1a237e]" />
                              ) : (
                                <Square className="h-4 w-4 text-gray-300" />
                              )}
                            </span>
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {emp?.employee_code || '-'}
                          </TableCell>
                          <TableCell className="text-sm">
                            {emp?.nickname || '-'}
                          </TableCell>
                          <TableCell className="text-sm">
                            {emp
                              ? [emp.first_name_en, emp.last_name_en].filter(Boolean).join(' ') ||
                                [emp.first_name_th, emp.last_name_th].filter(Boolean).join(' ')
                              : '-'}
                          </TableCell>
                          <TableCell className="text-xs text-gray-600">
                            {emp?.department || '-'}
                          </TableCell>
                          <TableCell className="text-right text-xs font-mono">
                            {formatCurrency(entry.base_salary)}
                          </TableCell>
                          <TableCell className="text-right text-xs font-mono">
                            {formatCurrency(entry.total_income)}
                          </TableCell>
                          <TableCell className="text-right text-xs font-mono text-red-600">
                            {formatCurrency(entry.total_deduction)}
                          </TableCell>
                          <TableCell className="text-right text-sm font-semibold font-mono">
                            {formatCurrency(entry.net_pay)}
                          </TableCell>
                          <TableCell className="text-center">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                if (!selectedPeriod) return
                                generateSalarySlip(entryToSlipData(entry, selectedPeriod))
                              }}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-[#1a237e] text-white rounded hover:bg-[#283593] transition-colors"
                              title="Download salary slip"
                            >
                              <Download className="h-3 w-3" />
                              Slip
                            </button>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Bottom summary */}
            {entries.length > 0 && (
              <div className="mt-4 flex flex-wrap items-center justify-between gap-4 text-sm text-gray-600">
                <div>
                  {selectedIds.size > 0 ? (
                    <span className="text-[#1a237e] font-medium">
                      {selectedIds.size} of {entries.length} selected
                    </span>
                  ) : (
                    <span>{entries.length} employee{entries.length !== 1 ? 's' : ''}</span>
                  )}
                </div>
                <div className="flex gap-6 text-xs">
                  <span>
                    Total Net Pay:{' '}
                    <strong className="text-gray-900">
                      {formatCurrency(entries.reduce((sum, e) => sum + (e.net_pay || 0), 0))}
                    </strong>
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
